import { createContext, useContext, ReactNode, useState, useCallback } from 'react';

export type Language = 'nl' | 'en';

interface LanguageContextType {
  language: Language;
  t: (key: string) => string;
  changeLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_KEY = 'msp_vergelijker_language';

// Translations for both languages
const translations: Record<Language, Record<string, string>> = {
  nl: {
    // Navigation
    'nav.home': 'Home',
    'nav.compare': 'Vergelijken',
    'nav.howItWorks': 'Zo werkt het',
    'nav.forMSPs': 'Voor MSPs',
    'nav.academy': 'Academy',
    'nav.about': 'Over ons',
    'nav.contact': 'Contact',
    'nav.more': 'Meer',
    'nav.login': 'Inloggen',
    'nav.logout': 'Uitloggen',
    'nav.findMSP': 'Vind mijn MSP',
    'nav.account': 'Account',
    'nav.mspDashboard': 'MSP Dashboard',
    'nav.adminDashboard': 'Admin Dashboard',
    
    // Common
    'common.search': 'Zoeken',
    'common.filter': 'Filter',
    'common.clear': 'Wissen',
    'common.save': 'Opslaan',
    'common.cancel': 'Annuleren',
    'common.close': 'Sluiten',
    'common.loading': 'Laden...',
    'common.error': 'Er is iets misgegaan',
    'common.success': 'Succes',
    'common.readMore': 'Lees meer',
    'common.viewAll': 'Bekijk alles',
    'common.comingSoon': 'Binnenkort',
    
    // Location search
    'location.search': 'Zoek op locatie',
    'location.placeholder': 'Plaatsnaam...',
    'location.useCurrentLocation': 'Gebruik huidige locatie',
    'location.notSupported': 'Geolocatie wordt niet ondersteund door uw browser.',
    'location.found': 'Locatie gevonden',
    'location.notFound': 'Locatie niet gevonden',
    'location.notAvailable': 'Locatie niet beschikbaar',
    'location.checkPermissions': 'Controleer of locatietoegang is ingeschakeld.',
    'location.resultsFiltered': 'Resultaten worden gefilterd op basis van uw locatie.',
    'location.sortedByDistance': 'Zoekresultaten worden gesorteerd op afstand',
    'location.within': 'Binnen',
    
    // Theme
    'theme.dark': 'Donker thema',
    'theme.light': 'Licht thema',
    'theme.system': 'Systeemthema',
    
    // Footer
    'footer.rights': 'Alle rechten voorbehouden',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Voorwaarden',
    'footer.cookies': 'Cookies',

    // 404 Page
    '404.title': 'Pagina niet gevonden',
    '404.description': 'De pagina die je zoekt bestaat niet of is verplaatst.',
    '404.searchHint': 'Of zoek direct naar een MSP:',
    '404.popularMSPs': 'Populaire MSP\'s',
    '404.toHomepage': 'Naar homepage',
    '404.searchMSPs': 'MSP\'s zoeken',
    '404.faq': 'Veelgestelde vragen',

    // Contact Page
    'contact.title': 'Neem contact op',
    'contact.description': 'Heb je vragen, feedback of wil je je aanmelden als MSP? We horen graag van je.',
    'contact.phone': 'Telefoon',
    'contact.phoneComingSoon': 'Telefonisch contact wordt binnenkort mogelijk',
    'contact.faq.title': 'Veelgestelde vragen',
    'contact.faq.responseTime': 'Hoe snel krijg ik reactie?',
    'contact.faq.responseTimeAnswer': 'Wij streven ernaar binnen 24 uur te reageren op werkdagen.',
    'contact.faq.mspSignup': 'Hoe kan ik mijn MSP aanmelden?',
    'contact.faq.mspSignupAnswer': 'Selecteer "MSP-aanmelding" als onderwerp en vul het formulier in. Wij nemen contact met u op voor de volgende stappen.',
    'contact.faq.costs': 'Zijn er kosten verbonden aan het platform?',
    'contact.faq.costsAnswer': 'Voor MKB-bedrijven is het platform volledig gratis. MSP\'s kunnen kiezen uit gratis en betaalde profielen.',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.compare': 'Compare',
    'nav.howItWorks': 'How it works',
    'nav.forMSPs': 'For MSPs',
    'nav.academy': 'Academy',
    'nav.about': 'About us',
    'nav.contact': 'Contact',
    'nav.more': 'More',
    'nav.login': 'Log in',
    'nav.logout': 'Log out',
    'nav.findMSP': 'Vind mijn MSP',
    'nav.account': 'Account',
    'nav.mspDashboard': 'MSP Dashboard',
    'nav.adminDashboard': 'Admin Dashboard',
    
    // Common
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.clear': 'Clear',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.loading': 'Loading...',
    'common.error': 'Something went wrong',
    'common.success': 'Success',
    'common.readMore': 'Read more',
    'common.viewAll': 'View all',
    'common.comingSoon': 'Coming soon',
    
    // Location search
    'location.search': 'Search by location',
    'location.placeholder': 'City name...',
    'location.useCurrentLocation': 'Use current location',
    'location.notSupported': 'Geolocation is not supported by your browser.',
    'location.found': 'Location found',
    'location.notFound': 'Location not found',
    'location.notAvailable': 'Location not available',
    'location.checkPermissions': 'Check if location access is enabled.',
    'location.resultsFiltered': 'Results are filtered based on your location.',
    'location.sortedByDistance': 'Search results are sorted by distance',
    'location.within': 'Within',
    
    // Theme
    'theme.dark': 'Dark theme',
    'theme.light': 'Light theme',
    'theme.system': 'System theme',
    
    // Footer
    'footer.rights': 'All rights reserved',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms',
    'footer.cookies': 'Cookies',

    // 404 Page
    '404.title': 'Page not found',
    '404.description': 'The page you are looking for does not exist or has been moved.',
    '404.searchHint': 'Or search for an MSP directly:',
    '404.popularMSPs': 'Popular MSPs',
    '404.toHomepage': 'Go to homepage',
    '404.searchMSPs': 'Search MSPs',
    '404.faq': 'FAQ',

    // Contact Page
    'contact.title': 'Contact us',
    'contact.description': 'Do you have questions, feedback or want to sign up as an MSP? We\'d love to hear from you.',
    'contact.phone': 'Phone',
    'contact.phoneComingSoon': 'Phone contact will be available soon',
    'contact.faq.title': 'Frequently asked questions',
    'contact.faq.responseTime': 'How quickly will I get a response?',
    'contact.faq.responseTimeAnswer': 'We aim to respond within 24 hours on business days.',
    'contact.faq.mspSignup': 'How can I register my MSP?',
    'contact.faq.mspSignupAnswer': 'Select "MSP registration" as the subject and fill out the form. We will contact you for the next steps.',
    'contact.faq.costs': 'Are there any costs associated with the platform?',
    'contact.faq.costsAnswer': 'For SME businesses, the platform is completely free. MSPs can choose from free and paid profiles.',
  },
};

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LANGUAGE_KEY);
      if (stored === 'en' || stored === 'nl') return stored;
      
      // Detect browser language
      const browserLang = navigator.language.split('-')[0];
      return browserLang === 'en' ? 'en' : 'nl';
    }
    return 'nl';
  });

  const t = useCallback((key: string): string => {
    return translations[language][key] || translations['nl'][key] || key;
  }, [language]);

  const changeLanguage = useCallback((lang: Language) => {
    setLanguage(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_KEY, lang);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
